import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, ArrowLeft, Share2, Calendar, Download, Target } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import SocialShare from './SocialShare';

const AIQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [currentStep, setCurrentStep] = useState('hero'); // hero, quiz, results
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [gateAnswers, setGateAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [score, setScore] = useState(0);
  const [resultTier, setResultTier] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Parse URL to determine current state
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    if (params.tier) {
      const tier = params.tier;
      const scoreParam = searchParams.get('score');
      
      if (tier && scoreParam) {
        setResultTier(tier);
        setScore(parseInt(scoreParam));
        setCurrentStep('results');
      }
    } else if (params.questionNumber) {
      const questionNum = parseInt(params.questionNumber) - 1;
      if (questionNum >= 0 && questionNum < questions.length) {
        setCurrentQuestion(questionNum);
        setCurrentStep('quiz');
      }
    } else if (path === '/ai-quiz') {
      setCurrentStep('hero');
    }
  }, [location, params]);

  const questions = [
    {
      id: 'q1',
      question: 'How much of your day is spent on repeatable, rules-based tasks?',
      icon: '🔄',
      options: [
        { text: 'Less than 10%', points: 0 },
        { text: '10–30%', points: 1 },
        { text: '30–60%', points: 2 },
        { text: 'More than 60%', points: 3 }
      ]
    },
    {
      id: 'q2',
      question: 'Your workflow documents & data are…',
      icon: '📊',
      options: [
        { text: 'Scattered across stray drives', points: 0 },
        { text: 'Mostly cloud-based but messy', points: 1 },
        { text: 'Organized in shared tools', points: 2 },
        { text: 'Fully structured & API-friendly', points: 3 }
      ]
    },
    {
      id: 'q3',
      question: 'Which best describes your current use of AI tools?',
      icon: '🤖',
      options: [
        { text: '"AI? Isn\'t that sci-fi?"', points: 0 },
        { text: 'Dabbled with ChatGPT once', points: 1 },
        { text: 'Use a few AI-powered apps regularly', points: 2 },
        { text: 'We build or fine-tune our own models', points: 3 }
      ]
    },
    {
      id: 'q4',
      question: 'If a key employee quit tomorrow, how easily could you replace their output with tech?',
      icon: '👥',
      options: [
        { text: 'Impossible', points: 0 },
        { text: 'Would scramble', points: 1 },
        { text: 'Could patch with SaaS', points: 2 },
        { text: 'Largely automated already', points: 3 }
      ]
    },
    {
      id: 'q5',
      question: 'Customer interactions today are…',
      icon: '💬',
      options: [
        { text: '100% human-to-human', points: 0 },
        { text: 'Some chatbots/IVR', points: 1 },
        { text: 'AI assists reps or self-serve flows', points: 2 },
        { text: 'Mostly autonomous with human QC', points: 3 }
      ]
    },
    {
      id: 'q6',
      question: 'Your competitive edge primarily comes from…',
      icon: '🏆',
      options: [
        { text: 'Personal relationships', points: 0 },
        { text: 'Unique knowledge', points: 1 },
        { text: 'Proprietary data/processes', points: 2 },
        { text: 'Rapid tech adoption & iteration', points: 3 }
      ]
    },
    {
      id: 'q7',
      question: 'How regulated is your industry?',
      icon: '⚖️',
      options: [
        { text: 'Heavily', points: 0 },
        { text: 'Moderately', points: 1 },
        { text: 'Light', points: 2 },
        { text: 'Virtually none', points: 3 }
      ]
    },
    {
      id: 'q8',
      question: 'Gut check: When you hear "AI replaces jobs," you…',
      icon: '🎯',
      options: [
        { text: 'Panic', points: 0 },
        { text: 'Feel uneasy', points: 1 },
        { text: 'See opportunities', points: 2 },
        { text: 'Already re-skilling the team', points: 3 }
      ]
    }
  ];

  const gateQuestions = [
    {
      id: 'business',
      question: 'Which best describes your business?',
      options: ['SaaS', 'E-commerce', 'Professional services', 'Brick-and-mortar retail', 'Manufacturing', 'Other']
    },
    {
      id: 'size',
      question: 'Company size?',
      options: ['Solo', '2–10', '11–50', '51–200', '200+']
    },
    {
      id: 'function',
      question: 'Job function?',
      options: ['Owner/Founder', 'C-suite', 'Operations', 'Marketing', 'Sales', 'Finance', 'HR', 'IT/Tech', 'Other']
    }
  ];

  const resultTiers = {
    safe: {
      range: [0, 6],
      tier: 'Safe… for now',
      headline: 'Your role is future-proof (ish)',
      copy: 'Good news! Your current role has a low automation threat today. You\'re working in areas that require human judgment, creativity, or complex relationship management. However, don\'t get too comfortable—AI is advancing rapidly. The key is to stay curious and keep learning. Start exploring how AI tools can augment your work rather than replace it. Consider this your wake-up call to begin building AI literacy now, while you have the luxury of time.',
      primaryCTA: 'Get Started with Sales Intercept',
      color: 'green',
      emoji: '🛡️'
    },
    augment: {
      range: [7, 13],
      tier: 'Augment & Thrive',
      headline: 'AI is your new sidekick',
      copy: 'You\'re in the sweet spot! Your role will evolve rather than disappear. AI will handle the routine parts of your job, freeing you to focus on strategy, creativity, and high-value decision-making. The winners in this category will be those who embrace AI as a powerful assistant. Start experimenting with AI tools in your daily workflow. Learn to prompt effectively, automate repetitive tasks, and use AI insights to make better decisions faster.',
      primaryCTA: 'Try Sales Intercept Free',
      color: 'blue',
      emoji: '🤝'
    },
    change: {
      range: [14, 18],
      tier: 'Change or Be Changed',
      headline: 'Big waves incoming',
      copy: 'Significant portions of your workflow are ripe for automation. This isn\'t necessarily bad news—it\'s a clear signal that you need to evolve your role proactively. The businesses and professionals who thrive will be those who get ahead of this wave. Focus on developing skills that complement AI: strategic thinking, emotional intelligence, complex problem-solving, and AI management. You have a window of opportunity to reinvent yourself before the market forces change.',
      primaryCTA: 'Start Your Free Trial',
      color: 'orange',
      emoji: '🌊'
    },
    alert: {
      range: [19, 24],
      tier: 'High Alert',
      headline: 'Automate or evaporate',
      copy: 'Your current role model is highly exposed to AI disruption. This requires immediate, decisive action. The good news? You\'re getting this wake-up call now, not after your industry has already transformed. You need to either become the person who implements AI solutions or pivot to roles that leverage uniquely human capabilities. Consider this your quarter to make bold moves: upskill aggressively, explore new roles, or even start building AI-powered solutions yourself.',
      primaryCTA: 'Get Sales Intercept Now',
      color: 'red',
      emoji: '🚨'
    }
  };

  const calculateScore = () => {
    const totalScore = Object.values(answers).reduce((sum, points) => sum + points, 0);
    setScore(totalScore);
    
    let tier;
    if (totalScore <= 6) tier = 'safe';
    else if (totalScore <= 13) tier = 'augment';
    else if (totalScore <= 18) tier = 'change';
    else tier = 'alert';
    
    setResultTier(tier);
    
    // Navigate to results URL
    navigate(`/ai-quiz/results/${tier}?score=${totalScore}`);
  };

  const handleAnswerSelect = (questionId, points) => {
    setAnswers(prev => ({ ...prev, [questionId]: points }));
    
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        navigate(`/ai-quiz/question/${nextQuestion + 1}`);
      } else {
        // Skip gate step and go directly to results
        calculateScore();
        setCurrentStep('results');
      }
    }, 300);
  };

  const handleGateAnswer = (questionId, answer) => {
    setGateAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleStartQuiz = () => {
    setCurrentStep('quiz');
    navigate('/ai-quiz/question/1');
  };

  const handleBackNavigation = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      navigate(`/ai-quiz/question/${prevQuestion + 1}`);
    } else {
      setCurrentStep('hero');
      navigate('/ai-quiz');
    }
  };

  const progress = currentStep === 'quiz' ? ((currentQuestion + 1) / questions.length) * 100 : 100;

  // Generate shareable URL for results
  const getShareableUrl = () => {
    if (resultTier && score !== null) {
      return `${window.location.origin}/ai-quiz/results/${resultTier}?score=${score}`;
    }
    return window.location.href;
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (email && Object.keys(gateAnswers).length === 3) {
      setShowContactForm(false);
      // Here you would typically send the data to your backend
      alert('Thank you! Your personalized action plan will be sent to your email shortly.');
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {currentStep === 'results' && resultTier 
            ? `AI Risk Assessment: ${resultTiers[resultTier].tier} - Sales Intercept`
            : 'Will AI Take My Job? - 8-Question Reality Check | Sales Intercept'
          }
        </title>
        <meta name="description" content="Take our 8-question AI risk assessment to discover how automation might impact your role. Get your personalized AI readiness score and action plan." />
        <meta property="og:title" content={
          currentStep === 'results' && resultTier 
            ? `I scored ${score}/24 on the AI Risk Assessment: ${resultTiers[resultTier].tier}`
            : 'Will AI Take My Job? - Find Out Your Risk Level'
        } />
        <meta property="og:description" content="8-question reality check for busy business professionals. Get your AI risk score now!" />
        <meta property="og:image" content="https://media.salesintercept.com/ai-quiz-og.jpg" />
        <meta property="og:url" content={getShareableUrl()} />
        <link rel="canonical" href={getShareableUrl()} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100">
        {/* Progress Bar */}
        {currentStep !== 'hero' && (
          <div className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
            <div className="h-3 bg-gray-200">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-si to-aqua-si"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        )}

        <div className="container mx-auto px-4 py-8">
          <AnimatePresence mode="wait">
            {/* Hero Section */}
            {currentStep === 'hero' && (
              <motion.div
                key="hero"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-4xl mx-auto text-center pt-8"
              >
                <div className="text-8xl mb-8">🤖</div>
                <h1 className="text-4xl md:text-6xl font-black mb-6 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Will AI Take My Job?
                </h1>
                <h2 className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
                  8-Question Reality Check for Busy Business Pros
                </h2>
                <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                  Get your personalized AI risk score and discover whether you should panic, 
                  pivot, or party. Takes less than 3 minutes.
                </p>
                <motion.button
                  onClick={handleStartQuiz}
                  className="bg-gradient-to-r from-purple-si to-aqua-si text-white px-8 py-4 rounded-full text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Quiz <ChevronRight className="inline ml-2" />
                </motion.button>
              </motion.div>
            )}

            {/* Quiz Questions */}
            {currentStep === 'quiz' && (
              <motion.div
                key="quiz"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="max-w-3xl mx-auto pt-8"
              >
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                  <div className="flex items-center justify-between mb-8">
                    <button
                      onClick={handleBackNavigation}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft className="w-6 h-6" />
                    </button>
                    <span className="text-sm font-medium text-gray-500">
                      Question {currentQuestion + 1} of {questions.length}
                    </span>
                  </div>

                  <div className="text-center mb-8">
                    <div className="text-6xl mb-4">{questions[currentQuestion].icon}</div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                      {questions[currentQuestion].question}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {questions[currentQuestion].options.map((option, index) => (
                      <motion.button
                        key={index}
                        onClick={() => handleAnswerSelect(questions[currentQuestion].id, option.points)}
                        className="w-full p-4 text-left bg-gray-50 hover:bg-purple-50 rounded-xl border-2 border-transparent hover:border-purple-200 transition-all duration-200"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="font-medium text-gray-900">{option.text}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Results */}
            {currentStep === 'results' && resultTier && (
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="max-w-4xl mx-auto overflow-y-auto pt-8"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className={`bg-gradient-to-r ${
                    resultTiers[resultTier].color === 'green' ? 'from-green-400 to-green-600' :
                    resultTiers[resultTier].color === 'blue' ? 'from-blue-400 to-blue-600' :
                    resultTiers[resultTier].color === 'orange' ? 'from-orange-400 to-orange-600' :
                    'from-red-400 to-red-600'
                  } text-white p-8 text-center`}>
                    <div className="text-6xl mb-4">{resultTiers[resultTier].emoji}</div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-2">
                      {resultTiers[resultTier].headline}
                    </h2>
                    <p className="text-xl opacity-90 mb-4">
                      AI Risk Level: {resultTiers[resultTier].tier}
                    </p>
                    <div className="text-2xl font-bold">
                      Your Score: {score}/24
                    </div>
                  </div>

                  <div className="p-8 md:p-12 overflow-y-auto">
                    <p className="text-lg text-gray-700 leading-relaxed mb-8">
                      {resultTiers[resultTier].copy}
                    </p>

                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default AIQuiz;